// 引入 React 常用 Hook。
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// 引入全局 action 类型与命名空间。
import { ActionTypeValues, namespace } from "@/context";
// 引入帖子列表项类型。
import { IThreadLisItem } from "@/api/thread/list";
// 引入通知消息接口与类型。
import { getNotificationList, INotificationItem } from "@/api/message";
// 引入用户中心作品接口。
import { getUserCenterThreads, USER_CENTER_THREAD_TYPE } from "@/api/userCenterThreads";
// 引入互动操作类型枚举。
import { USER_ACTION_TYPE } from "@/components/UserAction";
// 引入分页常量。
import { USER_CENTER_MESSAGE_PAGE_SIZE, USER_CENTER_PAGE_SIZE } from "./constants";

// Hook 入参类型定义。
interface Params {
  initialWorks: IThreadLisItem[];
  initialUserInfo: any;
  account: any;
  userStats: any;
  displayAccount: any;
  isSelfView: boolean;
  activeTab: string;
  dispatch: (action: any) => void;
  programmaticScrollRef: React.MutableRefObject<boolean>;
  suppressAutoLoadUntilUserScrollRef: React.MutableRefObject<boolean>;
  restoredScrollTopRef: React.MutableRefObject<number | null>;
  threadStatePatchRef: React.MutableRefObject<Record<string, Partial<IThreadLisItem>>>;
}

// PC 用户中心列表逻辑 Hook（帖子/点赞/收藏/消息分页与本地状态同步）。
export const useUserCenterPcListLogic = ({
  initialWorks,
  initialUserInfo,
  account,
  userStats,
  displayAccount,
  isSelfView,
  activeTab,
  dispatch,
  programmaticScrollRef,
  suppressAutoLoadUntilUserScrollRef,
  restoredScrollTopRef,
  threadStatePatchRef,
}: Params) => {
  // 帖子列表状态（首屏数据去重后初始化）。
  const [postsData, setPostsData] = useState<IThreadLisItem[]>(() => {
    const seen = new Set<string>();
    return initialWorks.reduce((acc, current) => {
      const key = String(current.tid);
      if (!seen.has(key)) {
        seen.add(key);
        return acc.concat([current]);
      }
      return acc;
    }, [] as IThreadLisItem[]);
  });
  const [postsLoading, setPostsLoading] = useState(false);
  // 根据首屏总数推导是否有更多。
  const [postsHasMore, setPostsHasMore] = useState(() => {
    if (userStats?.totalWorks !== undefined) {
      return initialWorks.length < userStats.totalWorks;
    }
    return initialWorks.length >= USER_CENTER_PAGE_SIZE;
  });
  const [postsInitialized, setPostsInitialized] = useState(true);

  // 点赞列表状态。
  const [likesData, setLikesData] = useState<IThreadLisItem[]>([]);
  const [likesLoading, setLikesLoading] = useState(false);
  const [likesHasMore, setLikesHasMore] = useState(true);
  const [likesInitialized, setLikesInitialized] = useState(false);

  // 收藏列表状态。
  const [collectionsData, setCollectionsData] = useState<IThreadLisItem[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsHasMore, setCollectionsHasMore] = useState(true);
  const [collectionsInitialized, setCollectionsInitialized] = useState(false);

  // 消息列表状态。
  const [messagesData, setMessagesData] = useState<INotificationItem[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesHasMore, setMessagesHasMore] = useState(true);
  const [messagesInitialized, setMessagesInitialized] = useState(false);
  // 记录消息数据快照，避免异步闭包读旧值。
  const messagesDataRef = useRef<INotificationItem[]>([]);
  // 首屏帖子快照签名，避免重复重置 postsData。
  const postsSnapshotSignatureRef = useRef("");
  // 消息刷新并发锁，避免重复刷新未读逻辑。
  const messageRefreshInFlightRef = useRef(false);

  // 同步消息状态到 ref。
  useEffect(() => {
    messagesDataRef.current = messagesData;
  }, [messagesData]);

  // 首屏帖子变化时重新构建 postsData 并叠加 patch。
  useEffect(() => {
    const seen = new Set<string>();
    const uniqueWorks = initialWorks.reduce((acc, current) => {
      const key = String(current.tid);
      if (!seen.has(key)) {
        seen.add(key);
        return acc.concat([current]);
      }
      return acc;
    }, [] as IThreadLisItem[]);
    const ownerId = String(initialUserInfo?.id || account?.id || "");
    const totalWorks = typeof userStats?.totalWorks === "number" ? userStats.totalWorks : -1;
    const worksSignature = uniqueWorks.map((item) => String(item.tid)).join(",");
    const nextSnapshotSignature = `${ownerId}|${totalWorks}|${worksSignature}`;
    if (postsSnapshotSignatureRef.current === nextSnapshotSignature) return;
    postsSnapshotSignatureRef.current = nextSnapshotSignature;

    const patchedUniqueWorks = uniqueWorks.map((item) => {
      const patch = threadStatePatchRef.current[String(item.tid)];
      return patch ? { ...item, ...patch } : item;
    });

    setPostsData(patchedUniqueWorks);
    setPostsHasMore(() => {
      if (userStats?.totalWorks !== undefined) {
        return uniqueWorks.length < userStats.totalWorks;
      }
      return uniqueWorks.length >= USER_CENTER_PAGE_SIZE;
    });
    setPostsInitialized(true);
    setPostsLoading(false);
  }, [initialWorks, userStats?.totalWorks, initialUserInfo?.id, account?.id, threadStatePatchRef]);

  // 加载更多帖子。
  const loadMorePosts = useCallback(async () => {
    if (postsLoading || !postsHasMore) return;
    setPostsLoading(true);
    try {
      const offset = postsData.length;
      const page = offset === 0 ? 1 : Math.floor((offset - 1) / USER_CENTER_PAGE_SIZE) + 2;
      const uid = displayAccount?.id || account?.id;
      if (!uid) {
        setPostsLoading(false);
        return;
      }
      const res = await getUserCenterThreads({
        uid: Number(uid),
        type: USER_CENTER_THREAD_TYPE.MY_POSTS,
        page,
        page_size: USER_CENTER_PAGE_SIZE,
      });
      const newItems = (res.items || []).map((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return patch ? { ...item, ...patch } : item;
      });
      const total = res.total;
      let mergedLength = postsData.length;
      let appendedCount = 0;
      setPostsData((prev) => {
        const existingIds = new Set(prev.map((item) => String(item.tid)));
        const uniqueNewItems: IThreadLisItem[] = [];
        for (const item of newItems) {
          const key = String(item.tid);
          if (!existingIds.has(key)) {
            existingIds.add(key);
            uniqueNewItems.push(item);
          }
        }
        appendedCount = uniqueNewItems.length;
        mergedLength = prev.length + uniqueNewItems.length;
        return [...prev, ...uniqueNewItems];
      });
      setPostsInitialized(true);
      if (typeof total === "number") {
        setPostsHasMore(mergedLength < total);
      } else {
        setPostsHasMore(appendedCount >= USER_CENTER_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Failed to load more posts:", error);
    } finally {
      setPostsLoading(false);
    }
  }, [postsLoading, postsHasMore, postsData.length, displayAccount?.id, account?.id, threadStatePatchRef]);

  // 加载更多点赞。
  const loadMoreLikes = useCallback(async () => {
    if (likesLoading || !likesHasMore) return;
    setLikesLoading(true);
    try {
      const offset = likesInitialized ? likesData.length : 0;
      const page = offset === 0 ? 1 : Math.floor((offset - 1) / USER_CENTER_PAGE_SIZE) + 2;
      const uid = displayAccount?.id || account?.id;
      if (!uid) {
        setLikesLoading(false);
        return;
      }
      const res = await getUserCenterThreads({
        uid: Number(uid),
        type: USER_CENTER_THREAD_TYPE.LIKED,
        page,
        page_size: USER_CENTER_PAGE_SIZE,
      });
      const patchedNewItems = (res.items || []).map((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return patch ? { ...item, ...patch } : item;
      });
      const validPatchedItems = patchedNewItems.filter((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return !(patch && patch.praised === false);
      });
      const total = res.total;
      let mergedLength = likesData.length;
      let appendedCount = 0;
      setLikesData((prev) => {
        const existingIds = new Set(prev.map((item) => String(item.tid)));
        const uniqueNewItems: IThreadLisItem[] = [];
        for (const item of validPatchedItems) {
          const key = String(item.tid);
          if (!existingIds.has(key)) {
            existingIds.add(key);
            uniqueNewItems.push(item);
          }
        }
        appendedCount = uniqueNewItems.length;
        mergedLength = prev.length + uniqueNewItems.length;
        return [...prev, ...uniqueNewItems];
      });
      setLikesInitialized(true);
      if (typeof total === "number") {
        setLikesHasMore(mergedLength < total);
      } else {
        setLikesHasMore(appendedCount >= USER_CENTER_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Failed to load more likes:", error);
    } finally {
      setLikesLoading(false);
    }
  }, [likesLoading, likesHasMore, likesInitialized, likesData.length, displayAccount?.id, account?.id, threadStatePatchRef]);

  // 加载更多收藏。
  const loadMoreCollections = useCallback(async () => {
    if (collectionsLoading || !collectionsHasMore) return;
    setCollectionsLoading(true);
    try {
      const offset = collectionsInitialized ? collectionsData.length : 0;
      const page = offset === 0 ? 1 : Math.floor((offset - 1) / USER_CENTER_PAGE_SIZE) + 2;
      const uid = displayAccount?.id || account?.id;
      if (!uid) {
        setCollectionsLoading(false);
        return;
      }
      const res = await getUserCenterThreads({
        uid: Number(uid),
        type: USER_CENTER_THREAD_TYPE.FAVORITE,
        page,
        page_size: USER_CENTER_PAGE_SIZE,
      });
      const patchedNewItems = (res.items || []).map((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return patch ? { ...item, ...patch } : item;
      });
      const validPatchedItems = patchedNewItems.filter((item) => {
        const patch = threadStatePatchRef.current[String(item.tid)];
        return !(patch && patch.favorite === false);
      });
      const total = res.total;
      let mergedLength = collectionsData.length;
      let appendedCount = 0;
      setCollectionsData((prev) => {
        const existingIds = new Set(prev.map((item) => String(item.tid)));
        const filteredNewItems = validPatchedItems.filter((item) => !existingIds.has(String(item.tid)));
        appendedCount = filteredNewItems.length;
        mergedLength = prev.length + filteredNewItems.length;
        return [...prev, ...filteredNewItems];
      });
      setCollectionsInitialized(true);
      if (typeof total === "number") {
        setCollectionsHasMore(mergedLength < total);
      } else {
        setCollectionsHasMore(appendedCount >= USER_CENTER_PAGE_SIZE);
      }
    } catch (error) {
      console.error("Failed to load more collections:", error);
    } finally {
      setCollectionsLoading(false);
    }
  }, [collectionsLoading, collectionsHasMore, collectionsInitialized, collectionsData.length, displayAccount?.id, account?.id, threadStatePatchRef]);

  // 加载更多消息。
  const loadMoreMessages = useCallback(async () => {
    if (messagesLoading || !messagesHasMore || messageRefreshInFlightRef.current) return;
    setMessagesLoading(true);
    try {
      const currentItems = messagesDataRef.current;
      const offset = currentItems.length;
      const res = await getNotificationList({ offset, limit: USER_CENTER_MESSAGE_PAGE_SIZE });
      const newItems = res.items || [];
      const total = res.total_items;
      const existingIds = new Set(currentItems.map((item) => String(item.id)));
      const filteredNewItems = newItems.filter((item) => !existingIds.has(String(item.id)));
      const mergedLength = currentItems.length + filteredNewItems.length;
      setMessagesData((prev) => {
        const prevIds = new Set(prev.map((item) => String(item.id)));
        const appendItems = filteredNewItems.filter((item) => !prevIds.has(String(item.id)));
        return [...prev, ...appendItems];
      });
      setMessagesInitialized(true);
      setMessagesHasMore(typeof total === "number" ? mergedLength < total : filteredNewItems.length >= USER_CENTER_MESSAGE_PAGE_SIZE);
    } catch (error) {
      console.error("Failed to load more messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  }, [messagesLoading, messagesHasMore]);

  // 重新拉取消息第一页。
  const reloadMessages = useCallback(async () => {
    setMessagesLoading(true);
    try {
      const res = await getNotificationList({ offset: 0, limit: USER_CENTER_MESSAGE_PAGE_SIZE });
      const newItems = res.items || [];
      const total = res.total_items;
      setMessagesData(newItems);
      setMessagesHasMore(typeof total === "number" ? newItems.length < total : newItems.length >= USER_CENTER_MESSAGE_PAGE_SIZE);
    } catch (error) {
      console.error("Failed to reload messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  // 刷新消息并将未读数本地重置为 0。
  const refreshMessagesAndResetUnread = useCallback(
    async (options?: { reloadList?: boolean }) => {
      if (!(account?.messages && account.messages > 0)) return;
      if (messageRefreshInFlightRef.current) return;
      messageRefreshInFlightRef.current = true;
      try {
        if (options?.reloadList) {
          await reloadMessages();
          setMessagesInitialized(true);
        }
        if (typeof window !== "undefined" && Number(account?.messages) > 0) {
          (window as any).__USER_CENTER_UNREAD_FORCE_SYNC__ = true;
        }
        dispatch({
          type: ActionTypeValues.UPDATE_ACCOUNT,
          namespace: namespace.app,
          payload: { ...account, messages: 0 },
        });
      } finally {
        messageRefreshInFlightRef.current = false;
      }
    },
    [account, dispatch, reloadMessages]
  );

  // 点赞/收藏成功后的本地 patch 同步。
  const handleItemUpdate = useCallback((item: IThreadLisItem, type: USER_ACTION_TYPE, newStatus: boolean) => {
    const patchId = String(item.tid);
    const existingPatch = threadStatePatchRef.current[patchId] || {};
    let newPraiseCount = item.praise || 0;
    let newFavCount = item.favtimes || 0;
    if (type === USER_ACTION_TYPE.PRAISE) {
      const currentPraise = existingPatch.praise !== undefined ? existingPatch.praise : item.praise || 0;
      newPraiseCount = newStatus ? currentPraise + 1 : Math.max(0, currentPraise - 1);
    } else {
      const currentFav = existingPatch.favtimes !== undefined ? existingPatch.favtimes : item.favtimes || 0;
      newFavCount = newStatus ? currentFav + 1 : Math.max(0, currentFav - 1);
    }
    const newPatch: Partial<IThreadLisItem> = {
      ...existingPatch,
      ...(type === USER_ACTION_TYPE.PRAISE ? { praised: newStatus, praise: newPraiseCount } : { favorite: newStatus, favtimes: newFavCount }),
    };
    threadStatePatchRef.current[patchId] = newPatch;
    const patchedItem = { ...item, ...existingPatch, ...newPatch };

    const updateList = (list: IThreadLisItem[], isTargetList: boolean) => {
      let found = false;
      let newList = list.map((i) => {
        if (String(i.tid) === patchId) {
          found = true;
          return { ...i, ...newPatch };
        }
        return i;
      });
      if (isTargetList) {
        if (newStatus && !found) {
          newList = [patchedItem, ...newList];
        } else if (!newStatus && found) {
          const shouldDeferRemoval =
            type === USER_ACTION_TYPE.PRAISE || type === USER_ACTION_TYPE.COLLECT;
          if (!shouldDeferRemoval) {
            newList = newList.filter((i) => String(i.tid) !== patchId);
          }
        }
      }
      return newList;
    };

    // 同步更新帖子/点赞/收藏三个列表。
    setPostsData((prev) => updateList(prev, false));
    setLikesData((prev) => updateList(prev, type === USER_ACTION_TYPE.PRAISE));
    setCollectionsData((prev) => updateList(prev, type === USER_ACTION_TYPE.COLLECT));
  }, [threadStatePatchRef]);

  // 点赞页切换时过滤被取消点赞的项。
  useEffect(() => {
    if (activeTab !== "myLikes") return;
    setLikesData((prev) => prev.filter((item) => threadStatePatchRef.current[String(item.tid)]?.praised !== false));
  }, [activeTab, threadStatePatchRef]);

  // 收藏页切换时过滤被取消收藏的项。
  useEffect(() => {
    if (activeTab !== "myCollections") return;
    setCollectionsData((prev) => prev.filter((item) => threadStatePatchRef.current[String(item.tid)]?.favorite !== false));
  }, [activeTab, threadStatePatchRef]);

  // 按 activeTab 懒加载对应列表。
  useEffect(() => {
    if (activeTab === "myPosts" && !postsInitialized && !postsLoading) loadMorePosts();
    if (activeTab === "myLikes" && !likesInitialized && !likesLoading) loadMoreLikes();
    if (activeTab === "myCollections" && !collectionsInitialized && !collectionsLoading) loadMoreCollections();
    if (activeTab === "messages" && !messagesInitialized && !messagesLoading) loadMoreMessages();
  }, [activeTab, postsInitialized, postsLoading, likesInitialized, likesLoading, collectionsInitialized, collectionsLoading, messagesInitialized, messagesLoading, loadMorePosts, loadMoreLikes, loadMoreCollections, loadMoreMessages]);

  // 本人进入消息 tab 且有未读时，自动刷新并重置未读。
  useEffect(() => {
    if (!isSelfView || activeTab !== "messages") return;
    if (!(account?.messages && account.messages > 0)) return;
    refreshMessagesAndResetUnread({ reloadList: true });
  }, [isSelfView, activeTab, account?.messages, refreshMessagesAndResetUnread]);

  // 触底自动加载逻辑（并处理恢复滚动后的抑制区间）。
  useEffect(() => {
    const handleScroll = () => {
      if (programmaticScrollRef.current) return;
      if (activeTab !== "myPosts" && activeTab !== "myLikes" && activeTab !== "myCollections" && activeTab !== "messages") return;
      const nextElement = document.getElementById("__next");
      if (!nextElement) return;
      const scrollTop = nextElement.scrollTop;
      const clientHeight = nextElement.clientHeight;
      const scrollHeight = nextElement.scrollHeight;
      if (suppressAutoLoadUntilUserScrollRef.current) {
        const restoredTop = restoredScrollTopRef.current;
        if (typeof restoredTop === "number" && Math.abs(scrollTop - restoredTop) <= 2) return;
        suppressAutoLoadUntilUserScrollRef.current = false;
        restoredScrollTopRef.current = null;
      }
      if (scrollHeight - scrollTop - clientHeight < 200) {
        if (activeTab === "myPosts") loadMorePosts();
        else if (activeTab === "myLikes") loadMoreLikes();
        else if (activeTab === "myCollections") loadMoreCollections();
        else if (activeTab === "messages") loadMoreMessages();
      }
    };
    const nextElement = document.getElementById("__next");
    if (nextElement) nextElement.addEventListener("scroll", handleScroll);
    return () => {
      if (nextElement) nextElement.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, loadMorePosts, loadMoreLikes, loadMoreCollections, loadMoreMessages, programmaticScrollRef, suppressAutoLoadUntilUserScrollRef, restoredScrollTopRef]);

  // 从 postsData 里拆分草稿与已发布列表，供页面渲染层使用。
  const draftsViewModel = useMemo(() => {
    const d = postsData?.filter((item) => (item as any).displayorder === -4) || [];
    const p = postsData?.filter((item) => (item as any).displayorder !== -4) || [];
    return {
      drafts: d,
      publishedPosts: p,
      showDrafts: isSelfView && d.length > 0,
      showPosts: p.length > 0,
    };
  }, [postsData, isSelfView]);

  // 返回页面层消费的状态与操作方法。
  return {
    postsData,
    postsLoading,
    likesData,
    likesLoading,
    likesInitialized,
    collectionsData,
    collectionsLoading,
    collectionsInitialized,
    messagesData,
    messagesLoading,
    messagesInitialized,
    handleItemUpdate,
    ...draftsViewModel,
  };
};
